/**
 * Takumi Edge Co. — Build Your Own picker.
 * Finish + piece selection, live compare-at total, bulk add to cart.
 * Real discount is applied by a Shopify automatic discount at checkout —
 * the total shown here is an estimate using the same tier rules.
 *
 * Event handling uses delegation (one listener per event type on the
 * section root) rather than per-element listeners attached at init time.
 * This is deliberate: per-element attachment is fragile against duplicate
 * initialization or timing issues — if init runs twice, or runs before an
 * element exists, per-element listeners silently fail to attach and there's
 * no visible error. Delegation only needs the root element to exist once,
 * and re-running init is guarded against below, so this class of bug can't
 * recur even if something upstream (theme, app, extension) re-triggers init.
 */
(function () {
  'use strict';

  function formatMoney(cents, moneyFormat) {
    var amount = (cents / 100).toFixed(2);
    var format = moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  function discountForCount(count, tiers) {
    var pct = 0;
    (tiers || []).forEach(function (tier) {
      if (count >= tier.min && tier.pct > pct) {
        pct = tier.pct;
      }
    });
    return pct;
  }

  function initInstance(root) {
    if (root.hasAttribute('data-te-byo-initialized')) return;
    root.setAttribute('data-te-byo-initialized', 'true');

    var dataEl = root.querySelector('[data-te-byo-data]');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }

    var summaryList = root.querySelector('[data-te-byo-summary-list]');
    var compareRowEl = root.querySelector('[data-te-byo-compare-row]');
    var compareEl = root.querySelector('[data-te-byo-compare]');
    var totalEl = root.querySelector('[data-te-byo-total]');
    var discountPctEl = root.querySelector('[data-te-byo-discount-pct]');
    var savingsEl = root.querySelector('[data-te-byo-savings]');
    var bonusEl = root.querySelector('[data-te-byo-bonus]');
    var shippingEl = root.querySelector('[data-te-byo-shipping-note]');
    var shippingProgressEl = root.querySelector('[data-te-byo-shipping-progress]');
    var shippingFillEl = root.querySelector('[data-te-byo-shipping-fill]');
    var bonusProgressEl = root.querySelector('[data-te-byo-bonus-progress]');
    var bonusFillEl = root.querySelector('[data-te-byo-bonus-fill]');
    var addonCard = root.querySelector('[data-te-byo-addon]');
    var addonCheckbox = addonCard ? addonCard.querySelector('[data-te-byo-addon-checkbox]') : null;
    var addonPriceEl = addonCard ? addonCard.querySelector('[data-te-byo-addon-price]') : null;
    var addBtn = root.querySelector('[data-te-byo-add]');
    var mobileBar = root.querySelector('[data-te-byo-mobile-bar]');
    var mobileCountEl = root.querySelector('[data-te-byo-mobile-count]');
    var mobileTotalEl = root.querySelector('[data-te-byo-mobile-total]');
    var mobileAddBtn = root.querySelector('[data-te-byo-mobile-add]');

    var piecesById = {};
    data.pieces.forEach(function (p) {
      piecesById[p.id] = p;
    });

    // Assign piece ids by DOM order once, up front — same order the JSON was built in.
    var pieceCards = Array.prototype.slice.call(root.querySelectorAll('[data-te-byo-piece]'));
    pieceCards.forEach(function (card, index) {
      var piece = data.pieces[index];
      if (piece) card.setAttribute('data-piece-id', piece.id);
    });

    var currentFinish = (data.finishes[0] && data.finishes[0].key) || 'starter';
    // Set by recompute(), read by addToCart() — which bonus tier's paid add-on (if any)
    // is currently active. Kept separate from baseSpend so checking the add-on can never
    // itself push the customer over the free-gift threshold (see recompute() below).
    var activeAddon = null;

    function pieceInFinish(pieceId, finishKey) {
      var piece = piecesById[pieceId];
      if (!piece) return null;
      var entry = piece.prices[finishKey];
      // Gate on price only, not the storefront "available" flag — that flag has
      // been unreliable (stuck false on genuinely sellable variants). If something
      // is truly unsellable, the real /cart/add.js call surfaces a real error.
      if (!entry || entry.price === null) return null;
      return entry;
    }

    function allPieceCards() {
      return Array.prototype.slice.call(root.querySelectorAll('[data-te-byo-piece]'));
    }

    function allUpsellCards() {
      return Array.prototype.slice.call(root.querySelectorAll('[data-te-byo-upsell]'));
    }

    function syncCardsToFinish() {
      allPieceCards().forEach(function (card) {
        var id = card.getAttribute('data-piece-id');
        var entry = pieceInFinish(id, currentFinish);
        var checkbox = card.querySelector('[data-te-byo-checkbox]');
        var priceEl = card.querySelector('[data-te-byo-piece-price]');

        if (!entry) {
          card.hidden = true;
          if (checkbox) checkbox.checked = false;
          return;
        }
        card.hidden = false;
        if (priceEl) priceEl.textContent = formatMoney(entry.price * 100, data.moneyFormat);

        // Repoint the photo so the handle matches the selected finish. Not every
        // finish/piece combo has been photographed yet, so a failed load steps down:
        // per-finish shot -> the piece's default knife -> striped placeholder.
        // Showing the default knife beats an empty tile when only the handle differs.
        var img = card.querySelector('[data-te-byo-piece-img]');
        var fallback = card.querySelector('[data-te-byo-piece-fallback]');
        if (img && entry.img) {
          var defaultImg = img.getAttribute('data-default-img') || '';
          img.onerror = function () {
            if (defaultImg && img.getAttribute('src') !== defaultImg) {
              img.setAttribute('src', defaultImg);
              return;
            }
            img.hidden = true;
            if (fallback) fallback.hidden = false;
          };
          if (img.getAttribute('src') !== entry.img) {
            img.hidden = false;
            if (fallback) fallback.hidden = true;
            img.setAttribute('src', entry.img);
          } else if (img.complete && img.naturalWidth === 0) {
            // Already failed before this script ran (server-rendered src had no asset)
            img.onerror();
          }
        }
      });

      allUpsellCards().forEach(function (card) {
        var matches = card.getAttribute('data-finish-key') === currentFinish;
        var checkbox = card.querySelector('[data-te-byo-upsell-checkbox]');
        card.hidden = !matches;
        if (!matches && checkbox) checkbox.checked = false;
      });

      // Spec strip follows the selected finish. Layer count genuinely differs by
      // line (37 / 67 / 89), so this can't be one static row.
      root.querySelectorAll('[data-te-byo-spec]').forEach(function (row) {
        row.hidden = row.getAttribute('data-finish-key') !== currentFinish;
      });

      recompute();
    }

    function activeUpsell() {
      var card = allUpsellCards().filter(function (c) {
        return !c.hidden;
      })[0];
      if (!card) return null;
      var checkbox = card.querySelector('[data-te-byo-upsell-checkbox]');
      if (!checkbox || !checkbox.checked) return null;
      var match = (data.finishUpsells || []).filter(function (u) {
        return u.finishKey === currentFinish;
      })[0];
      if (!match) return null;
      return match;
    }

    function selectedPieces() {
      var selected = [];
      allPieceCards().forEach(function (card) {
        if (card.hidden) return;
        var checkbox = card.querySelector('[data-te-byo-checkbox]');
        if (!checkbox || !checkbox.checked) return;
        var id = card.getAttribute('data-piece-id');
        var entry = pieceInFinish(id, currentFinish);
        if (!entry) return;
        selected.push({
          id: id,
          name: card.getAttribute('data-name'),
          price: Number(entry.price),
          variantId: entry.variantId,
        });
      });
      return selected;
    }

    function recompute() {
      var selected = selectedPieces();
      var count = selected.length;
      var compareTotal = selected.reduce(function (sum, p) {
        return sum + p.price;
      }, 0);
      var pct = discountForCount(count, data.discountTiers);
      var estTotal = compareTotal * (1 - pct / 100);
      var savings = compareTotal - estTotal;

      var upsell = activeUpsell();
      var upsellPrice = upsell ? upsell.price : 0;

      // baseSpend excludes any paid sharpener add-on on purpose: eligibility for the
      // FREE gift must be decided before the add-on is considered, otherwise checking
      // the paid add-on could itself push spend over the threshold and make the item
      // it was just paid for now also free.
      var baseSpend = estTotal + upsellPrice;

      var qualifiedBonus = null;
      var nextBonus = null;
      (data.bonusTiers || [])
        .slice()
        .sort(function (a, b) {
          return a.threshold - b.threshold;
        })
        .forEach(function (bonus) {
          if (baseSpend >= bonus.threshold) {
            qualifiedBonus = bonus;
          } else if (!nextBonus) {
            nextBonus = bonus;
          }
        });

      // Add-on eligibility: only offered for the next not-yet-qualified tier, and only
      // if that tier has a paid add-on configured. Uncheck it the moment the customer's
      // base spend alone clears the threshold — at that point the item is free, a paid
      // add-on checkbox sitting there checked would double up or read as a bug.
      activeAddon = null;
      if (addonCard) {
        if (count > 0 && nextBonus && nextBonus.addonVariantId) {
          addonCard.hidden = false;
          if (addonPriceEl) addonPriceEl.textContent = formatMoney(nextBonus.addonPrice * 100, data.moneyFormat);
          if (addonCheckbox && addonCheckbox.checked) {
            activeAddon = { variantId: nextBonus.addonVariantId, price: Number(nextBonus.addonPrice), name: nextBonus.addonName };
          }
        } else {
          addonCard.hidden = true;
          if (addonCheckbox) addonCheckbox.checked = false;
        }
      }
      var addonPrice = activeAddon ? activeAddon.price : 0;

      summaryList.innerHTML = '';
      if (count === 0 && !upsell) {
        var li = document.createElement('li');
        li.className = 'te-byo__summary-empty';
        li.textContent = 'No pieces selected yet';
        summaryList.appendChild(li);
      } else {
        selected.forEach(function (p) {
          var row = document.createElement('li');
          row.className = 'te-byo__summary-item';
          var nameSpan = document.createElement('span');
          nameSpan.textContent = p.name;
          var priceSpan = document.createElement('span');
          priceSpan.textContent = formatMoney(p.price * 100, data.moneyFormat);
          row.appendChild(nameSpan);
          row.appendChild(priceSpan);
          summaryList.appendChild(row);
        });
        if (upsell) {
          var upsellRow = document.createElement('li');
          upsellRow.className = 'te-byo__summary-item';
          var upsellNameSpan = document.createElement('span');
          upsellNameSpan.textContent = upsell.name;
          var upsellPriceSpan = document.createElement('span');
          upsellPriceSpan.textContent = formatMoney(upsellPrice * 100, data.moneyFormat);
          upsellRow.appendChild(upsellNameSpan);
          upsellRow.appendChild(upsellPriceSpan);
          summaryList.appendChild(upsellRow);
        }
        if (activeAddon) {
          var addonRow = document.createElement('li');
          addonRow.className = 'te-byo__summary-item';
          var addonNameSpan = document.createElement('span');
          addonNameSpan.textContent = activeAddon.name;
          var addonPriceSpan = document.createElement('span');
          addonPriceSpan.textContent = formatMoney(activeAddon.price * 100, data.moneyFormat);
          addonRow.appendChild(addonNameSpan);
          addonRow.appendChild(addonPriceSpan);
          summaryList.appendChild(addonRow);
        }
      }

      totalEl.textContent = formatMoney((estTotal + upsellPrice + addonPrice) * 100, data.moneyFormat);

      if (count === 0) {
        if (compareRowEl) compareRowEl.hidden = true;
      } else {
        compareEl.textContent = formatMoney((compareTotal + upsellPrice + addonPrice) * 100, data.moneyFormat);
        if (compareRowEl) compareRowEl.hidden = false;
      }

      if (pct > 0) {
        if (discountPctEl) {
          discountPctEl.hidden = false;
          discountPctEl.textContent = pct + '% off';
        }
        savingsEl.hidden = false;
        savingsEl.textContent = 'Save ' + formatMoney(savings * 100, data.moneyFormat);
      } else {
        if (discountPctEl) discountPctEl.hidden = true;
        savingsEl.hidden = true;
      }

      if (qualifiedBonus) {
        bonusEl.hidden = false;
        bonusEl.textContent = 'Unlocked: ' + qualifiedBonus.label + ' (' + qualifiedBonus.valueText + ')';
        if (bonusProgressEl) {
          bonusProgressEl.hidden = false;
          bonusProgressEl.setAttribute('data-bonus-unlocked', '');
        }
        if (bonusFillEl) bonusFillEl.style.width = '100%';
      } else if (nextBonus) {
        bonusEl.hidden = false;
        var spendToGo = nextBonus.threshold - baseSpend;
        bonusEl.textContent =
          formatMoney(spendToGo * 100, data.moneyFormat) + ' more unlocks ' + nextBonus.label + ' (' + nextBonus.valueText + ')';
        if (bonusProgressEl) {
          bonusProgressEl.hidden = false;
          bonusProgressEl.removeAttribute('data-bonus-unlocked');
        }
        if (bonusFillEl) bonusFillEl.style.width = Math.min(100, (baseSpend / nextBonus.threshold) * 100) + '%';
      } else {
        bonusEl.hidden = true;
        if (bonusProgressEl) bonusProgressEl.hidden = true;
      }

      // Add-on spend counts toward free shipping (it's real spend); it deliberately
      // does not feed back into baseSpend/the bonus-tier check above.
      var totalWithAddon = baseSpend + addonPrice;

      if (shippingEl && data.freeShippingThreshold > 0) {
        var shippingPct = Math.min(100, (totalWithAddon / data.freeShippingThreshold) * 100);
        if (count === 0) {
          shippingEl.hidden = true;
          if (shippingProgressEl) shippingProgressEl.hidden = true;
        } else if (totalWithAddon >= data.freeShippingThreshold) {
          shippingEl.hidden = false;
          shippingEl.textContent = 'Free express shipping unlocked';
          if (shippingProgressEl) shippingProgressEl.hidden = false;
          if (shippingFillEl) shippingFillEl.style.width = '100%';
        } else {
          shippingEl.hidden = false;
          var away = data.freeShippingThreshold - totalWithAddon;
          shippingEl.textContent = formatMoney(away * 100, data.moneyFormat) + ' away from free express shipping';
          if (shippingProgressEl) shippingProgressEl.hidden = false;
          if (shippingFillEl) shippingFillEl.style.width = shippingPct + '%';
        }
      }

      var hasUnlinked = selected.some(function (p) {
        return !p.variantId;
      });

      if (count === 0) {
        addBtn.disabled = true;
        addBtn.textContent = 'Select at least 1 piece';
      } else if (hasUnlinked) {
        addBtn.disabled = true;
        addBtn.textContent = 'Coming soon — finalizing this piece';
      } else {
        addBtn.disabled = false;
        addBtn.textContent = 'Add ' + count + ' piece' + (count === 1 ? '' : 's') + ' to cart';
      }

      if (mobileBar) {
        if (count === 0) {
          mobileBar.hidden = true;
          root.classList.remove('te-byo--bar-visible');
        } else {
          mobileBar.hidden = false;
          root.classList.add('te-byo--bar-visible');
          mobileCountEl.textContent = count + ' piece' + (count === 1 ? '' : 's') + (upsell ? ' + set' : '') + (activeAddon ? ' + stone' : '');
          mobileTotalEl.textContent = formatMoney((estTotal + upsellPrice + addonPrice) * 100, data.moneyFormat);
          mobileAddBtn.disabled = hasUnlinked;
          mobileAddBtn.textContent = hasUnlinked ? 'Finalizing…' : 'Add to cart';
        }
      }

      root.dispatchEvent(
        new CustomEvent('te-byo:change', { detail: { count: count, compareTotal: compareTotal, estTotal: estTotal } })
      );
    }

    function addToCart() {
      var selected = selectedPieces();
      if (!selected.length) return;

      var items = selected.map(function (p) {
        return { id: p.variantId, quantity: 1 };
      });

      var compareTotal = selected.reduce(function (sum, p) {
        return sum + p.price;
      }, 0);
      var pct = discountForCount(selected.length, data.discountTiers);
      var estTotal = compareTotal * (1 - pct / 100);

      var upsell = activeUpsell();
      var upsellPrice = upsell ? upsell.price : 0;
      if (upsell && upsell.variantId) {
        items.push({ id: upsell.variantId, quantity: 1 });
      }

      var baseSpend = estTotal + upsellPrice;
      (data.bonusTiers || []).forEach(function (bonus) {
        if (baseSpend >= bonus.threshold && bonus.variantId) {
          items.push({ id: bonus.variantId, quantity: 1 });
        }
      });

      if (activeAddon) {
        items.push({ id: activeAddon.variantId, quantity: 1 });
      }

      var addUrl = (data.cartAddUrl || '/cart/add').replace(/\.js$/, '') + '.js';

      addBtn.disabled = true;
      var originalText = addBtn.textContent;
      addBtn.textContent = 'Adding…';
      if (mobileAddBtn) {
        mobileAddBtn.disabled = true;
        mobileAddBtn.textContent = 'Adding…';
      }

      // Request the cart sections back with the add, so the drawer can be re-rendered
      // with real contents. Without this the drawer keeps whatever HTML it had at page
      // load — which on a first add is the "your cart is empty" state, shown alongside
      // a real order total.
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (result) {
          if (result.status) {
            addBtn.textContent = result.description || 'Something went wrong — try again';
            addBtn.disabled = false;
            if (mobileAddBtn) {
              mobileAddBtn.textContent = 'Try again';
              mobileAddBtn.disabled = false;
            }
            return;
          }

          addBtn.textContent = 'Added to cart ✓';
          if (mobileAddBtn) mobileAddBtn.textContent = 'Added ✓';

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'takumi-build-your-own', cartData: result });
          }

          var drawer = document.querySelector('cart-drawer');

          // The theme's own renderer swaps in the returned section HTML, rebinds the
          // overlay and opens the drawer. Prefer it over hand-rolled DOM surgery.
          if (drawer && result.sections && typeof drawer.renderContents === 'function') {
            // renderContents only clears `is-empty` from `.drawer__inner`, but the class
            // is also set on the <cart-drawer> wrapper at render time — leave it and the
            // drawer keeps rendering its empty state over real cart contents.
            drawer.classList.remove('is-empty');
            drawer.renderContents(result);
          } else {
            // Fallback: no drawer (or an older theme build) — just refresh the count.
            fetch('/cart.js', { headers: { Accept: 'application/json' } })
              .then(function (r) {
                return r.json();
              })
              .then(function (cart) {
                document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(function (el) {
                  el.textContent = cart.item_count;
                });
              });
            if (drawer && typeof drawer.open === 'function') drawer.open();
          }

          setTimeout(function () {
            allPieceCards().forEach(function (card) {
              var checkbox = card.querySelector('[data-te-byo-checkbox]');
              if (checkbox) checkbox.checked = false;
            });
            allUpsellCards().forEach(function (card) {
              var checkbox = card.querySelector('[data-te-byo-upsell-checkbox]');
              if (checkbox) checkbox.checked = false;
            });
            if (addonCheckbox) addonCheckbox.checked = false;
            recompute();
            addBtn.textContent = originalText;
          }, 2200);
        })
        .catch(function () {
          addBtn.textContent = 'Something went wrong — try again';
          addBtn.disabled = false;
          if (mobileAddBtn) {
            mobileAddBtn.textContent = 'Try again';
            mobileAddBtn.disabled = false;
          }
        });
    }

    // --- Delegated event handling: one listener per event type on root. ---

    root.addEventListener('change', function (e) {
      var target = e.target;
      if (!target) return;

      if (target.matches && target.matches('.te-byo__finish-input')) {
        if (target.checked) {
          currentFinish = target.value;
          syncCardsToFinish();
        }
        return;
      }

      if (target.matches && target.matches('[data-te-byo-checkbox]')) {
        recompute();
        return;
      }

      if (target.matches && target.matches('[data-te-byo-upsell-checkbox]')) {
        recompute();
        return;
      }

      if (target.matches && target.matches('[data-te-byo-addon-checkbox]')) {
        recompute();
        return;
      }
    });

    root.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;

      if (target.closest('[data-te-byo-add]')) {
        if (!addBtn.disabled) addToCart();
        return;
      }

      if (target.closest('[data-te-byo-mobile-add]')) {
        if (mobileAddBtn && !mobileAddBtn.disabled) addToCart();
        return;
      }
    });

    syncCardsToFinish();
  }

  function initAll() {
    document.querySelectorAll('.te-byo').forEach(initInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
